/**
 * Copyright (c) 2025 eBUCIS, LLC. All rights reserved.
 *
 * InviteCoachModal - Modal for users to search and invite coaches
 */

import { useState, useEffect, useCallback } from 'react'
import { Search, X, UserPlus, GraduationCap, Star, Loader2 } from 'lucide-react'
import { Avatar } from '../profile'
import LoadingSpinner from '../LoadingSpinner'
import api from '../../services/api'
import toast from 'react-hot-toast'
import { createLogger } from '../../utils/logger'

const logger = createLogger('InviteCoachModal')

const InviteCoachModal = ({ isOpen, onClose, organizations, onInviteSent }) => {
  const [searchQuery, setSearchQuery] = useState('')
  const [searchResults, setSearchResults] = useState([])
  const [searching, setSearching] = useState(false)
  const [selectedOrg, setSelectedOrg] = useState('')
  const [inviting, setInviting] = useState(null)

  // Set default organization
  useEffect(() => {
    if (organizations?.length > 0 && !selectedOrg) {
      setSelectedOrg(organizations[0].id)
    }
  }, [organizations, selectedOrg])

  // Reset when modal opens/closes
  useEffect(() => {
    if (!isOpen) {
      setSearchQuery('')
      setSearchResults([])
    }
  }, [isOpen])

  // Debounced search
  useEffect(() => {
    if (!searchQuery || searchQuery.length < 2) {
      setSearchResults([])
      return
    }

    const timer = setTimeout(async () => {
      setSearching(true)
      try {
        const response = await api.get(`/user/search-coaches?q=${encodeURIComponent(searchQuery)}`)
        if (response.data.success) {
          setSearchResults(response.data.coaches)
        }
      } catch (error) {
        logger.error('Error searching coaches:', error)
      } finally {
        setSearching(false)
      }
    }, 300)

    return () => clearTimeout(timer)
  }, [searchQuery])

  const handleInvite = async (coach) => {
    if (!selectedOrg) {
      toast.error('Please select an organization')
      return
    }

    setInviting(coach.id)
    try {
      const response = await api.post('/user/invite-coach', {
        email: coach.email,
        organizationId: selectedOrg
      })

      if (response.data.success) {
        toast.success(response.data.message || 'Invitation sent!')
        onInviteSent?.()
        onClose()
      }
    } catch (error) {
      logger.error('Error inviting coach:', error)
      toast.error(error.response?.data?.message || 'Failed to send invitation')
    } finally {
      setInviting(null)
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-lg w-full mx-4 max-h-[80vh] flex flex-col">
        {/* Header */}
        <div className="p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center">
                <GraduationCap className="w-5 h-5 text-primary-600 dark:text-primary-400" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                  Find a Coach
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Search for coaches by name or email
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Search & Organization Selection */}
        <div className="p-4 border-b border-gray-200 dark:border-gray-700 space-y-3">
          {/* No organizations warning */}
          {(!organizations || organizations.length === 0) && (
            <div className="p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-700 rounded-lg">
              <p className="text-sm text-yellow-800 dark:text-yellow-200">
                You need to be part of an organization to invite a coach.
              </p>
            </div>
          )}

          {/* Organization selector */}
          {organizations?.length > 1 && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Organization
              </label>
              <select
                value={selectedOrg}
                onChange={(e) => setSelectedOrg(e.target.value)}
                className="input w-full"
              >
                {organizations.map((org) => (
                  <option key={org.id} value={org.id}>
                    {org.name}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Search input */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search by name or email..."
              className="input w-full pl-10"
              autoFocus
              disabled={!organizations || organizations.length === 0}
            />
            {searching && (
              <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 animate-spin" />
            )}
          </div>
        </div>

        {/* Results */}
        <div className="flex-1 overflow-y-auto p-4">
          {searchQuery.length < 2 ? (
            <div className="text-center py-8 text-gray-500 dark:text-gray-400">
              <Search className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p>Enter at least 2 characters to search</p>
            </div>
          ) : searchResults.length === 0 && !searching ? (
            <div className="text-center py-8 text-gray-500 dark:text-gray-400">
              <GraduationCap className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p>No coaches found matching "{searchQuery}"</p>
            </div>
          ) : (
            <div className="space-y-2">
              {searchResults.map((coach) => (
                <div
                  key={coach.id}
                  className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg"
                >
                  <div className="flex items-center gap-3">
                    <Avatar
                      type={coach.avatarType}
                      seed={coach.avatarSeed}
                      style={coach.avatarStyle}
                      name={`${coach.firstName || ''} ${coach.lastName || ''}`}
                      publicName={coach.publicName}
                      size="md"
                    />
                    <div>
                      <p className="font-medium text-gray-900 dark:text-gray-100">
                        {coach.firstName} {coach.lastName}
                      </p>
                      <p className="text-sm text-gray-500 dark:text-gray-400">{coach.email}</p>
                      {coach.coachProfile?.headline && (
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                          {coach.coachProfile.headline}
                        </p>
                      )}
                      {coach.coachProfile?.averageRating > 0 && (
                        <div className="flex items-center gap-1 mt-1">
                          <Star className="w-3 h-3 text-yellow-500 fill-yellow-500" />
                          <span className="text-xs text-gray-600 dark:text-gray-400">
                            {coach.coachProfile.averageRating.toFixed(1)} (
                            {coach.coachProfile.totalReviews} reviews)
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                  <button
                    onClick={() => handleInvite(coach)}
                    disabled={inviting === coach.id}
                    className="btn btn-sm btn-primary inline-flex items-center gap-1"
                  >
                    {inviting === coach.id ? (
                      <LoadingSpinner size="xs" />
                    ) : (
                      <UserPlus className="w-4 h-4" />
                    )}
                    Invite
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-gray-200 dark:border-gray-700">
          <button onClick={onClose} className="btn btn-secondary w-full">
            Close
          </button>
        </div>
      </div>
    </div>
  )
}

export default InviteCoachModal
